import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanRelatedClassList,
  summarizeYowanClassSchedules,
} from '../app/yowanContentClient';
import {
  buildStakeholderHeaderContent,
  getStakeholderClassApplicationsHref,
  getStakeholderClassHref,
  StakeholderGuardNotice,
  StakeholderSearchToolbar,
  StakeholderShell,
  STAKEHOLDER_PAGE_SIZE,
  useStakeholderGuard,
} from './StakeholderSeminarsPage';
import {
  AdminListPagination,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const getClassSearchText = (record = {}) => [
  record.title,
  record.organizerName,
  record.standardInstructor,
  record.nextSlotLabel,
  record.area,
].filter(Boolean).join(' ').toLowerCase();

const formatClassScheduleSummaryLine = (item = {}) =>
  [item.datetime, item.location, item.instructor].filter(Boolean).join(' / ') || '-';

const ClassScheduleSummaryCell = ({ record }) => {
  const summary = summarizeYowanClassSchedules(record, { limit: 2 });
  const items = Array.isArray(summary?.items) ? summary.items : [];

  if (items.length === 0) {
    return '-';
  }

  return (
    <div>
      {items.map((item) => (
        <p key={item.id || formatClassScheduleSummaryLine(item)} className="yowan-admin-resource-table__subline">
          {formatClassScheduleSummaryLine(item)}
        </p>
      ))}
      {summary.remainingCount ? (
        <p className="yowan-admin-resource-table__subline">ほか{summary.remainingCount}件</p>
      ) : null}
    </div>
  );
};

const StakeholderClassesPage = ({ guardOptions }) => {
  const content = yowanPageContent['/stakeholder/classes'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useStakeholderGuard(guardOptions);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({
    count: 0,
    pageSize: STAKEHOLDER_PAGE_SIZE,
    next: null,
    previous: null,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const headerContent = buildStakeholderHeaderContent(content, guard.currentUser);

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanRelatedClassList({
      page,
      pageSize: STAKEHOLDER_PAGE_SIZE,
    })
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        setRecords(payload.results || []);
        setPageInfo({
          count: payload.count || 0,
          pageSize: payload.pageSize || STAKEHOLDER_PAGE_SIZE,
          next: payload.next,
          previous: payload.previous,
        });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setRecords([]);
        setPageInfo({
          count: 0,
          pageSize: STAKEHOLDER_PAGE_SIZE,
          next: null,
          previous: null,
        });
        setLoadError(`${headerContent.title}を読み込めませんでした。`);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [guard.status, headerContent.title, page]);

  const visibleRecords = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return records;
    }

    return records.filter((record) => getClassSearchText(record).includes(normalizedQuery));
  }, [records, searchQuery]);

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  return (
    <StakeholderShell activeNavHref="/stakeholder/classes" routeKey="/stakeholder/classes" currentUser={guard.currentUser}>
      <AdminPageHeader title={headerContent.title} paragraphs={headerContent.paragraphs} />

      <StakeholderSearchToolbar
        searchPlaceholder={headerContent.searchPlaceholder}
        searchValue={searchQuery}
        onSearchChange={(event) => setSearchQuery(event.target.value)}
        metaText={`${visibleRecords.length}件表示`}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        {isLoading ? <LoadingSpinner label={`${headerContent.title}を読み込み中`} showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError && visibleRecords.length === 0 ? (
          <p className="yowan-admin-form__message">表示できる{headerContent.title}はありません。</p>
        ) : null}
        {!isLoading && !loadError && visibleRecords.length > 0 ? (
          <table className="yowan-admin-resource-table yowan-stakeholder-table yowan-stakeholder-table--classes">
            <thead>
              <tr>
                <th>教室・講座</th>
                <th>開催予定</th>
                <th>講師</th>
                <th>申込</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleRecords.map((record) => (
                <tr key={record.id}>
                  <td>
                    <p className="yowan-admin-resource-table__title">{record.title || '-'}</p>
                    {record.organizerName ? <p className="yowan-admin-resource-table__subline">主催: {record.organizerName}</p> : null}
                    {record.summary ? <p className="yowan-admin-resource-table__subline">{record.summary}</p> : null}
                  </td>
                  <td><ClassScheduleSummaryCell record={record} /></td>
                  <td>{record.standardInstructor || '-'}</td>
                  <td>{record.applicationsCount || 0}名</td>
                  <td>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={getStakeholderClassHref(record.id, preservedDomainSearch)}>詳細</Link>
                      <Link to={getStakeholderClassApplicationsHref(record.id, preservedDomainSearch)}>申込</Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      <AdminListPagination
        currentPage={page}
        totalPages={Math.max(1, Math.ceil((pageInfo.count || visibleRecords.length) / (pageInfo.pageSize || STAKEHOLDER_PAGE_SIZE)))}
        onPageChange={setPage}
      />
    </StakeholderShell>
  );
};

export default StakeholderClassesPage;
